import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Megaphone, Plus, Trash2, Eye, EyeOff, Check, X,
  Image as ImageIcon, ExternalLink, BarChart3, Upload,
  Clock, CheckCircle, XCircle, Edit2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Thumb qui gere image ET video MP4 automatiquement selon l'URL du media
// Compatible iOS Safari/iPad : attributs legacy inclus.
const AdMediaThumb = ({ url, alt, className }) => {
  if (!url) return null;
  const isVideo = /\.mp4($|\?)/i.test(url);
  const src = BACKEND_URL + url;
  if (isVideo) {
    return (
      <video
        src={src}
        className={className}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        // eslint-disable-next-line react/no-unknown-property
        webkit-playsinline="true"
        // eslint-disable-next-line react/no-unknown-property
        x5-playsinline="true"
      />
    );
  }
  return <img src={src} alt={alt} className={className} loading="lazy" />;
};

export default function AdminPublicites() {
  const [ads, setAds] = useState([]);
  const [pendingAds, setPendingAds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // "all", "pending", "stats"
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link_url: "",
    format: "banner",
    image_url: ""
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setIsLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API}/showcases`),
        axios.get(`${API}/showcases/pending`)
      ]);
      setAds(allRes.data);
      setPendingAds(pendingRes.data);
    } catch (error) {
      console.error("Error fetching ads:", error);
      toast.error("Erreur lors du chargement des publicités");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation côté client — évite un upload inutile qui échouera côté serveur
    const isVideo = file.type === "video/mp4";
    const isGif = file.type === "image/gif";
    const maxSize = isVideo ? 100 * 1024 * 1024 : isGif ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    const sizeLabel = isVideo ? "100 MB" : isGif ? "10 MB" : "5 MB";
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${sizeLabel})`);
      return;
    }

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("format", formData.format);

    try {
      const response = await axios.post(`${API}/ads/upload-image`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // 3 min pour supporter les vidéos MP4 lourdes sur mobile
      });
      setFormData({
        ...formData,
        image_url: response.data.image_url,
        media_type: response.data.media_type || (isVideo ? "video" : "image"),
      });
      toast.success(isVideo ? "Vidéo uploadée" : "Image uploadée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.image_url) {
      toast.error("Titre et image requis");
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append("title", formData.title);
    submitFormData.append("description", formData.description || "");
    submitFormData.append("link_url", formData.link_url || "");
    submitFormData.append("format", formData.format);
    submitFormData.append("image_url", formData.image_url);
    submitFormData.append("created_by", "admin");

    try {
      await axios.post(`${API}/showcases`, submitFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Publicité créée et activée");
      setShowCreateForm(false);
      setFormData({ title: "", description: "", link_url: "", format: "banner", image_url: "" });
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de création");
    }
  };

  const handleToggleAd = async (adId) => {
    try {
      const response = await axios.put(`${API}/showcases/${adId}/toggle`);
      toast.success(response.data.is_active ? "Publicité activée" : "Publicité désactivée");
      fetchAds();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleApproveAd = async (adId) => {
    try {
      await axios.put(`${API}/showcases/${adId}/approve`);
      toast.success("Publicité approuvée et activée");
      fetchAds();
    } catch (error) {
      toast.error("Erreur d'approbation");
    }
  };

  const handleRejectAd = async (adId) => {
    const reason = prompt("Raison du rejet (optionnel):");
    try {
      await axios.put(`${API}/showcases/${adId}/reject?reason=${encodeURIComponent(reason || "")}`);
      toast.success("Publicité rejetée");
      fetchAds();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm("Supprimer cette publicité ?")) return;
    try {
      await axios.delete(`${API}/showcases/${adId}`);
      toast.success("Publicité supprimée");
      fetchAds();
    } catch (error) {
      toast.error("Erreur de suppression");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-100 text-yellow-800", icon: Clock, label: "En attente" },
      approved: { bg: "bg-green-100 text-green-800", icon: CheckCircle, label: "Approuvée" },
      rejected: { bg: "bg-red-100 text-red-800", icon: XCircle, label: "Rejetée" }
    };
    return badges[status] || badges.pending;
  };

  // Stats calculation
  const totalViews = ads.reduce((sum, ad) => sum + (ad.views || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
  const activeCount = ads.filter(ad => ad.is_active && ad.status === "approved").length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Megaphone className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Total publicités</p>
                <p className="text-2xl font-bold">{ads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Actives</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Vues totales</p>
                <p className="text-2xl font-bold">{totalViews.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ExternalLink className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Clics totaux</p>
                <p className="text-2xl font-bold">{totalClicks.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex rounded-full bg-gray-100 p-1">
          <Button
            size="sm"
            variant={activeTab === "all" ? "default" : "ghost"}
            className="rounded-full"
            onClick={() => setActiveTab("all")}
          >
            Toutes ({ads.length})
          </Button>
          <Button
            size="sm"
            variant={activeTab === "pending" ? "default" : "ghost"}
            className="rounded-full"
            onClick={() => setActiveTab("pending")}
          >
            En attente ({pendingAds.length})
          </Button>
        </div>

        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="rounded-full bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle publicité
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Créer une publicité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateAd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Titre de la publicité"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="format">Format</Label>
                  <select
                    id="format"
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                  >
                    <option value="banner">Bannière (1200x400)</option>
                    <option value="square">Carré (600x600)</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description courte"
                />
              </div>

              <div>
                <Label htmlFor="link_url">Lien (URL)</Label>
                <Input
                  id="link_url"
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label>Média *</Label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <div className="flex gap-4 items-start">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex-shrink-0"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingImage ? "Upload en cours…" : "Choisir image ou vidéo"}
                  </Button>
                  {formData.image_url && (
                    <div className="relative">
                      {formData.media_type === "video" || /\.mp4$/i.test(formData.image_url) ? (
                        <video
                          src={BACKEND_URL + formData.image_url}
                          className="h-20 rounded-lg object-cover"
                          muted
                          autoPlay
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          src={BACKEND_URL + formData.image_url}
                          alt="Preview"
                          className="h-20 rounded-lg object-cover"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: "", media_type: undefined })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Formats acceptés : JPG, PNG, WebP (5 MB), GIF (10 MB), MP4 vidéo (100 MB)
                </p>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  Créer et activer
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Pending Ads (for approval) */}
      {activeTab === "pending" && pendingAds.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader className="bg-yellow-50">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <Clock className="w-5 h-5" />
              Publicités en attente de validation ({pendingAds.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {pendingAds.map((ad) => (
                <div key={ad.id} className="p-4 flex items-center gap-4">
                  {ad.image_url && (
                    <AdMediaThumb
                      url={ad.image_url}
                      alt={ad.title}
                      className="w-24 h-16 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium">{ad.title}</h4>
                    <p className="text-sm text-gray-500">{ad.description}</p>
                    <p className="text-xs text-gray-400">
                      Format: {ad.format === "banner" ? "Bannière" : "Carré"} •
                      Soumis le {new Date(ad.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 rounded-full"
                      onClick={() => handleApproveAd(ad.id)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approuver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 rounded-full"
                      onClick={() => handleRejectAd(ad.id)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Rejeter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Ads List */}
      {activeTab === "all" && (
        <Card>
          <CardHeader>
            <CardTitle>Toutes les publicités</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center">Chargement...</div>
            ) : ads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Aucune publicité. Créez-en une pour commencer!
              </div>
            ) : (
              <div className="divide-y">
                {ads.map((ad) => {
                  const statusBadge = getStatusBadge(ad.status);
                  const StatusIcon = statusBadge.icon;
                  return (
                    <div key={ad.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                      {ad.image_url && (
                        <AdMediaThumb
                          url={ad.image_url}
                          alt={ad.title}
                          className="w-32 h-20 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{ad.title}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${statusBadge.bg}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusBadge.label}
                          </span>
                          {ad.is_active && ad.status === "approved" && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{ad.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                          <span>{ad.format === "banner" ? "Bannière" : "Carré"}</span>
                          <span>👁 {ad.views || 0} vues</span>
                          <span>🔗 {ad.clicks || 0} clics</span>
                          {ad.link_url && (
                            <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                              Voir lien
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {ad.status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleAd(ad.id)}
                            className="rounded-full"
                          >
                            {ad.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 rounded-full"
                          onClick={() => handleDeleteAd(ad.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
